import { Dialog } from '@reach/dialog'
import styled from 'styled-components'

import { HeadlineText } from '@/ui/atoms/headline-text'
import { applyResponsiveStyle } from '@/ui/helpers/applyResponsiveStyle'
import { ResponsiveProp } from '@/ui/typings/helpers'

type StyledModalProps = {
  $height: ResponsiveProp<string>
  $isDarkModal?: boolean
  $margin: ResponsiveProp<string>
  $maxHeight?: ResponsiveProp<string>
  $maxWidth?: ResponsiveProp<string>
  $padding: ResponsiveProp<string>
  $width: ResponsiveProp<string>
  $overflow?: string
  $borderRadius?: string
}

const getResponsiveWidth = (value: StyledModalProps['$width']) => `
  width: ${value};
`
const getResponsiveHeight = (value: StyledModalProps['$height']) => `
  height: ${value};
`
const getResponsiveMaxHeight = (value: StyledModalProps['$maxHeight']) => `
  max-height: ${value};
`
const getResponsiveMaxWidth = (value: StyledModalProps['$maxWidth']) => `
  max-width: ${value};
`
const getResponsivePadding = (value: StyledModalProps['$padding']) => `
  padding: ${value};
`
const getResponsiveMargin = (value: StyledModalProps['$margin']) => `
  margin: ${value};
`

export const StyledModal = styled(Dialog)<StyledModalProps>(
  ({
    theme,
    $height,
    $isDarkModal,
    $margin,
    $maxHeight = '',
    $maxWidth = '',
    $padding,
    $width,
    $overflow,
    $borderRadius = theme.borderRadius.small,
  }) => `
  ${applyResponsiveStyle($width, theme, getResponsiveWidth)}
  ${applyResponsiveStyle($padding, theme, getResponsivePadding)}
  ${applyResponsiveStyle($maxWidth, theme, getResponsiveMaxWidth)}
  ${applyResponsiveStyle($maxHeight, theme, getResponsiveMaxHeight)}
  ${applyResponsiveStyle($margin, theme, getResponsiveMargin)}
  ${applyResponsiveStyle($height, theme, getResponsiveHeight)}
    
  background: ${$isDarkModal ? 'rgba(47, 47, 47, 0.99)' : theme.colors.white};
  border-radius: ${$borderRadius};
  box-shadow: ${theme.boxShadow['shadow-2']};
  display: flex;
  flex-direction: column;
  overflow: ${$overflow};
  position: relative;

  & > button {
    z-index: 1;
  }
`
)

export const StyledModalCloseButton = styled.button<{
  isDarkModal?: boolean
  hasTitle?: boolean
}>(
  ({ theme, isDarkModal, hasTitle }) => `
  align-items: center;
  background-color: transparent;
  border-radius: ${theme.borderRadius.rounded};
  display: flex;
  height: ${theme.spacing.xLarge};
  justify-content: center;
  position: absolute;
  right: ${hasTitle ? '1.8rem' : '1.1rem'};
  top: ${hasTitle ? '1.8rem' : '1.1rem'};
  width: ${theme.spacing.xLarge};
  
  & > svg {
    color:  ${isDarkModal ? theme.colors.white : 'current'};
    height: ${isDarkModal ? theme.spacing.giant : theme.spacing.large};
    width: ${isDarkModal ? theme.spacing.giant : theme.spacing.large};
  }
`
)

export const StyledModalHeading = styled(HeadlineText)`
  ${({ theme }) => `
  padding: ${theme.spacing.large} ${theme.spacing.large};
  font-weight: ${theme.fontWeight.regular};
  border-bottom: 0.1rem solid ${theme.colors.grey.light4};
  `}
`

export const StyledModalHeader = styled.header`
  padding: ${({ theme }) => theme.spacing.default} 0;
`

export const StyledModalBody = styled.div`
  overflow: auto;
`

export const StyledModalFooter = styled.footer`
  padding-top: ${({ theme }) => theme.spacing.default};
`
